import React from 'react';
import {
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Chip,
  Box
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const mineralTypes = [
  'Gold',
  'Silver',
  'Copper',
  'Iron',
  'Zinc',
  'Lead',
  'Nickel',
  'Cobalt',
  'Lithium',
  'Rare Earth Elements',
  'Diamonds',
  'Other'
];

const certifications = [
  'ISO 14001',
  'OHSAS 18001',
  'Responsible Minerals Assurance Process (RMAP)',
  'Fairmined',
  'Fairtrade',
  'Responsible Jewellery Council (RJC)',
  'Kimberley Process',
  'Other'
];

const MinerRegistrationForm = ({ onSubmit, loading }) => {
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      companyName: '',
      role: '',
      mineralTypes: [],
      miningLocations: '',
      productionCapacity: '',
      certifications: []
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('Required'),
      lastName: Yup.string().required('Required'),
      email: Yup.string().email('Invalid email').required('Required'),
      password: Yup.string()
        .min(8, 'Must be at least 8 characters')
        .required('Required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Required'),
      companyName: Yup.string().required('Required'),
      role: Yup.string().required('Required'),
      mineralTypes: Yup.array().min(1, 'Select at least one mineral type')
    }),
    onSubmit: (values) => {
      // Add stakeholder type
      onSubmit({ ...values, stakeholderType: 'miner' });
    }
  });

  return (
    <form onSubmit={formik.handleSubmit} className="p-4">
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="firstName"
            name="firstName"
            label="First Name"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="lastName"
            name="lastName"
            label="Last Name"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email Address"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="password"
            name="password"
            label="Password"
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="companyName"
            name="companyName"
            label="Company Name"
            value={formik.values.companyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.companyName && Boolean(formik.errors.companyName)}
            helperText={formik.touched.companyName && formik.errors.companyName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="role"
            name="role"
            label="Your Role in the Company"
            value={formik.values.role}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.role && Boolean(formik.errors.role)}
            helperText={formik.touched.role && formik.errors.role}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl
            fullWidth
            error={formik.touched.mineralTypes && Boolean(formik.errors.mineralTypes)}
          >
            <InputLabel id="mineralTypes-label">Mineral Types</InputLabel>
            <Select
              labelId="mineralTypes-label"
              id="mineralTypes"
              name="mineralTypes"
              multiple
              value={formik.values.mineralTypes}
              onChange={(e) => {
                formik.handleChange(e);
                // Show a temporary message when a mineral is selected
                if (e.target.value.length > formik.values.mineralTypes.length) {
                  const newValue = e.target.value.find(v => !formik.values.mineralTypes.includes(v));
                  const tempElement = document.getElementById('mineral-selection-feedback');
                  if (tempElement) {
                    tempElement.textContent = `Added: ${newValue}`;
                    tempElement.style.opacity = 1;
                    setTimeout(() => {
                      tempElement.style.opacity = 0;
                    }, 2000);
                  }
                }
              }}
              onBlur={formik.handleBlur}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {mineralTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.mineralTypes && formik.errors.mineralTypes && (
              <FormHelperText>{formik.errors.mineralTypes}</FormHelperText>
            )}
            <Box
              id="mineral-selection-feedback"
              sx={{
                mt: 1,
                color: 'success.main',
                fontWeight: 'medium',
                opacity: 0,
                transition: 'opacity 0.5s ease-in-out'
              }}
            >
            </Box>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="miningLocations"
            name="miningLocations"
            label="Mining Locations (comma separated)"
            value={formik.values.miningLocations}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.miningLocations && Boolean(formik.errors.miningLocations)}
            helperText={formik.touched.miningLocations && formik.errors.miningLocations}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="productionCapacity"
            name="productionCapacity"
            label="Production Capacity (tons/year)"
            value={formik.values.productionCapacity}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.productionCapacity && Boolean(formik.errors.productionCapacity)}
            helperText={formik.touched.productionCapacity && formik.errors.productionCapacity}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="certifications-label">Certifications</InputLabel>
            <Select
              labelId="certifications-label"
              id="certifications"
              name="certifications"
              multiple
              value={formik.values.certifications}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {certifications.map((cert) => (
                <MenuItem key={cert} value={cert}>
                  {cert}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            className="gradient-bg"
            sx={{ mt: 2, py: 1.5 }}
          >
            {loading ? 'Registering...' : 'Register as Miner'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default MinerRegistrationForm;
